/**
 * Ship picker + data-freshness pills.
 *
 * Owns boot: on mount, load the ship list for the active data source, then
 * restore a portable link, legacy query, or this tab's saved workspace. The
 * <select> drives column loading via `loadColumns`. Freshness pills (historical
 * only) show the latest data timestamp and cache refresh time.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  ships,
  selectedShip,
  shipsLoading,
  shipDateRange,
  metadataUpdatedAt,
  activeDataSource,
  activeSection,
  activeTab,
} from '../../state/store.ts';
import { loadShips, loadColumns } from './ship-data.ts';
import {
  legacyViewQuery,
  readWorkingView,
  restoreFromUrl,
} from '../../state/url-sync.ts';
import {
  chartIdFromLocation,
  resolveLocationView,
  shareIdFromLocation,
} from '../../state/share-link.ts';
import { parseAppRoute, queryWithAppRoute } from '../../state/app-route.ts';
import { maybeAutoLoadDefaultView } from '../../state/presets.ts';
import { isPlotEnabled } from '../../state/plot-state.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { plotFetching } from '../plot/plot-ui-state.ts';
import { timeAgo } from '../../utils/dates.ts';
import '../plot/plot-ship-picker.ts';
import './ship-multi-select.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';

/**
 * Boot restores the launch URL or this tab's saved workspace **once per page
 * load**, not once per picker. The chart builder mounts a second picker inside
 * its dialog; when every mount booted, that second boot read Explore's URL back
 * into the atoms a moment after the builder had loaded the tile - so "Edit
 * chart" showed Explore's chart, and saving wrote it over the tile.
 */
let booted = false;

@customElement('ship-picker')
export class ShipPicker extends LightElement {
  private store = new StoreController(this, [
    ships,
    selectedShip,
    shipsLoading,
    shipDateRange,
    metadataUpdatedAt,
    activeDataSource,
    activeTab,
  ]);

  override connectedCallback(): void {
    super.connectedCallback();
    if (booted) return;
    booted = true;
    void this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    // Portable links win, then an explicit legacy query, then this tab's saved
    // draft. A clean route is overlaid last so `/explore/export` still means
    // Export even if the draft was saved while Plot was open.
    const hadPermalink = !!(shareIdFromLocation() || chartIdFromLocation());
    const linked = await resolveLocationView();
    const legacy = legacyViewQuery(window.location.search);
    const cleanRoute = parseAppRoute(window.location.pathname);
    const hadVisibleQuery = [...new URLSearchParams(window.location.search).keys()].length > 0;
    // A broken permalink must not silently open an unrelated draft from this
    // tab. It already showed the unavailable-link message and reset the path.
    const working = hadPermalink ? null : readWorkingView();
    const hadRestoredView = !!(linked || legacy || working);
    // An explicit destination, even without chart state, must not be replaced
    // by the user's default saved view. Preview flags behaved this way before
    // clean routes and remain authoritative too.
    const skipDefaultView = !!(
      hadPermalink ||
      legacy ||
      working ||
      cleanRoute ||
      hadVisibleQuery
    );
    let query = linked?.query ?? legacy ?? working ?? '';
    query = queryWithAppRoute(
      query,
      linked ? '/explore/plot' : window.location.pathname,
    );

    await loadShips();
    await restoreFromUrl(query, linked ? { visibleUrl: linked.visibleUrl } : {});
    // If neither a link nor this tab's draft carried a view, auto-load the
    // user's default view.
    await maybeAutoLoadDefaultView(skipDefaultView);

    // A shared link should show its chart, not a Plot button. Saved views
    // already do this in `loadView`; this is the same behaviour for a URL.
    // Deliberately here rather than inside `restoreFromUrl`, which the
    // assistant's apply path also calls and which plots for itself - putting it
    // there would fire a second, duplicate fetch for every proposal.
    if (
      hadRestoredView &&
      activeSection.value === 'explore' &&
      activeTab.value === 'plot' &&
      isPlotEnabled() &&
      !plotFetching.value
    ) {
      void doPlot();
    }
  }

  private renderFreshness(): TemplateResult | typeof nothing {
    // The metadata stamp is EOS's catalogue; other sources have none.
    if (activeDataSource.value !== DEFAULT_SOURCE) return nothing;
    const range = shipDateRange.value;
    if (!range?.max_date) return nothing;
    const refreshed = metadataUpdatedAt.value
      ? timeAgo(metadataUpdatedAt.value)
      : 'unknown';
    // Plain inline text, not a card - it isn't clickable, so it shouldn't look
    // like the interactive date-preset boxes next to it.
    return html`
      <div class="data-freshness visible" id="data-freshness">
        <span class="freshness-label">Latest data</span>
        <span class="freshness-value">${range.max_date}</span>
        <span class="freshness-sep">·</span>
        <span class="freshness-label">metadata refreshed</span>
        <span class="freshness-value">${refreshed}</span>
      </div>
    `;
  }

  override render(): TemplateResult {
    const loading = shipsLoading.value;

    return html`
      <div class="ship-freshness-row">
        <div class="ship-row">
          <label for="ship-select" class="ship-label">
            Ship
            ${loading
              ? html`<span class="spinner ship-spinner"></span>`
              : nothing}
          </label>
          <ship-multi-select></ship-multi-select>
        </div>
        <plot-ship-picker></plot-ship-picker>
        ${this.renderFreshness()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ship-picker': ShipPicker;
  }
}
